import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Building2, Search, RefreshCw, Plus, Trash2, X, Mail, Globe, Handshake,
  Archive, ArchiveRestore, AlertTriangle, Lock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  crmApi, type CrmClient, type CrmContact, type CrmRegistration, type CrmRemittance, type CrmUsageRecord, type CrmBillingContact,
  type OrgDivergence,
} from '../services/api';

/**
 * Organisations — clients, resellers and groups.
 *
 * This is the single point of truth for who an organisation IS. A **partner is
 * an account typed `reseller`**, not a separate model, which is why one page
 * administers both.
 *
 * Two relationships are recorded here and neither derives from the other:
 * **ownership** (`parentOrgId` — the group an account belongs to) and
 * **service** (`partnerOrgId` — the reseller that sells and services it). An
 * account can belong to one group and be sold by a reseller outside it.
 *
 * The service edge is the one that grants access: PMO mirrors it onto the RBAC
 * `organization` model, where a partner-over-client grant follows it. That is
 * why the picker offers resellers only and why a save reports what the mirror
 * did.
 *
 * Contacts are embedded rather than separate records: they are not useful
 * independently of the account, they are always read together, and the research
 * platform models them the same way. An update replaces the whole list, so the
 * editor always sends every contact.
 */

const ORG_TYPES: { value: string; label: string; hint: string }[] = [
  { value: 'client', label: 'Client', hint: 'Buys from us. Cannot service another organisation.' },
  { value: 'reseller', label: 'Reseller (partner)', hint: 'Sells and services accounts. This is what a partner is.' },
  { value: 'parent', label: 'Parent organisation', hint: 'One of our companies, or a holding organisation others roll up to. Parents can sell.' },
];

const RESIDENCY = [
  { value: '', label: '— not set —' },
  { value: 'south-africa', label: 'South Africa' },
  { value: 'europe', label: 'Europe' },
  { value: 'usa', label: 'USA' },
];

export default function ClientsPage() {
  const [clients, setClients] = useState<CrmClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<CrmClient | 'new' | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await crmApi.listClients(undefined, undefined,
        { includeArchived: true });
      setClients(data.clients || []);
    } catch (err: any) {
      toast.error(err.message || 'Could not load organisations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /** orgId -> account, for resolving the two edges to names. */
  const byOrgId = useMemo(() => {
    const m: Record<string, CrmClient> = {};
    clients.forEach((c) => { if (c.orgId) m[c.orgId.toUpperCase()] = c; });
    return m;
  }, [clients]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      if (!showArchived && c.status === 'archived') return false;
      if (typeFilter && (c.orgType || '') !== typeFilter) return false;
      if (!q) return true;
      return [c.name, c.industry, c.website, c.orgId].some(
        (v) => (v || '').toLowerCase().includes(q))
        || (c.contacts || []).some((p) =>
          [p.name, p.email].some((v) => (v || '').toLowerCase().includes(q)));
    });
  }, [clients, search, typeFilter, showArchived]);

  const archive = async (c: CrmClient) => {
    const restoring = c.status === 'archived';
    try {
      await crmApi.archiveClient(c._id, restoring);
      toast.success(restoring ? 'Restored' : 'Archived');
      load();
    } catch (err: any) {
      toast.error(err.message || 'Could not archive');
    }
  };

  /**
   * Delete is the exception, not the default. An `orgId` keys budgets, token
   * allowlists and idempotency namespaces in OTHER systems, so nothing here
   * fails loudly when one disappears — the server refuses a delete that would
   * orphan, and the reason it gives is worth reading rather than forcing past.
   */
  const remove = async (c: CrmClient) => {
    if (!confirm(`Delete "${c.name}" permanently? Archiving is usually what `
      + `you want — a deleted account still has to render on the quotes and `
      + `opportunities that reference it.`)) return;
    try {
      await crmApi.deleteClient(c._id);
      toast.success('Organisation deleted');
      load();
    } catch (err: any) {
      toast.error(err.message || 'Could not delete', { duration: 8000 });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Building2 size={20} className="text-accent" />
            Organisations
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Clients, resellers and groups. A partner is an account typed
            <strong> reseller</strong>. Quotes are raised against an account, and
            the reseller assigned here is what grants that partner sight of the
            work.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setEditing('new')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-accent text-white"
          >
            <Plus size={14} /> New organisation
          </button>
          <button
            onClick={load}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-accent hover:text-accent"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, orgId, industry, contact…"
            className="pl-8 pr-3 py-1.5 w-64 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent"
        >
          <option value="">All types</option>
          {ORG_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <input type="checkbox" checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)} />
          Show archived
        </label>
        <span className="text-xs text-gray-400 ml-auto">
          {filtered.length} of {clients.length}
        </span>
      </div>

      <MirrorPanel reload={load} />

      {clients.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Building2 size={44} className="text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-gray-600 dark:text-gray-300 font-medium">No organisations yet</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md">
            The research platform's client file was empty, so there was nothing to
            migrate — only the model. Add the first one.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((c) => (
            <Row key={c._id} client={c} byOrgId={byOrgId}
              onEdit={() => setEditing(c)} onArchive={() => archive(c)}
              onDelete={() => remove(c)} />
          ))}
        </div>
      )}

      {editing && (
        <ClientModal
          client={editing === 'new' ? null : editing}
          all={clients}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

/**
 * Where the CRM and the access model disagree.
 *
 * Silent when they agree — a permanently-visible "0 problems" panel is noise
 * that trains people to ignore the place a real problem will appear. The
 * endpoint is super-admin only and a 403 is swallowed: an account manager
 * cannot act on this and should not be shown a failure they cannot fix.
 *
 * Repair is a BUTTON, never automatic. A disagreement can legitimately be
 * resolved in either direction, and a job that always picks one is wrong about
 * half the time.
 */
function MirrorPanel({ reload }: { reload: () => void }) {
  const [rows, setRows] = useState<OrgDivergence[] | null>(null);
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    try {
      const data = await crmApi.orgDivergences();
      setRows(data.divergences || []);
    } catch {
      setRows(null);          // not permitted, or unreachable — stay quiet
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  if (!rows || rows.length === 0) return null;

  const adoptable = rows.filter((r) =>
    Object.values(r.fields || {}).some((f) => f.adoptable)).length;

  const apply = async () => {
    setBusy(true);
    try {
      const result = await crmApi.syncOrgs(true);
      const adopted = (result.adopted || []).length;
      toast.success(`${result.changed} organisation(s) updated`
        + (adopted ? `, ${adopted} existing edge(s) adopted onto the CRM` : '')
        + (result.failed ? `, ${result.failed} could not be resolved` : '') + '.');
      await check();
      reload();
    } catch (err: any) {
      toast.error(err.message || 'Could not sync');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-5 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
      <div className="flex items-start gap-2">
        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
            {rows.length} organisation{rows.length === 1 ? '' : 's'} out of step
            with the access model
          </p>
          <p className="text-xs text-amber-700 dark:text-amber-300/80 mt-0.5">
            The RBAC organisation carries the edge a partner-over-client grant
            follows. While these disagree, access does not match what the CRM says.
            {adoptable > 0 && (
              <> {adoptable} of them hold an edge research established before the
              CRM had the field — syncing RECORDS those on the account rather
              than clearing them.</>
            )}
          </p>
          <ul className="mt-2 space-y-1 text-xs text-amber-800 dark:text-amber-200">
            {rows.slice(0, 8).map((r) => (
              <li key={r.orgId} className="font-mono">
                {r.orgId} · {r.name} —{' '}
                {r.issue || Object.entries(r.fields || {}).map(([f, v]) => (
                  v.adoptable
                    ? `${f}: adopt "${v.rbac}" from the access model`
                    : `${f}: CRM "${v.crm || '—'}" vs access "${v.rbac || '—'}"`
                )).join('; ')}
              </li>
            ))}
            {rows.length > 8 && <li>…and {rows.length - 8} more</li>}
          </ul>
        </div>
        <button
          onClick={apply}
          disabled={busy}
          className="shrink-0 px-3 py-1.5 text-xs rounded-lg bg-amber-600 text-white disabled:opacity-50"
          title="Adopt existing edges onto the CRM, then rewrite the access edges from it"
        >
          {busy ? 'Syncing…' : 'Reconcile'}
        </button>
      </div>
    </div>
  );
}

const TYPE_STYLE: Record<string, string> = {
  reseller: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
  parent: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300',
  client: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
};

function Row({ client: c, byOrgId, onEdit, onArchive, onDelete }: {
  client: CrmClient;
  byOrgId: Record<string, CrmClient>;
  onEdit: () => void;
  onArchive: () => void;
  onDelete: () => void;
}) {
  const archived = c.status === 'archived';
  const partner = byOrgId[(c.partnerOrgId || '').toUpperCase()];
  const group = byOrgId[(c.parentOrgId || '').toUpperCase()];

  return (
    <div className={`rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 ${archived ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3">
        <button onClick={onEdit} className="min-w-0 flex-1 text-left">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {c.name}
            </span>
            <span className={`px-1.5 py-0.5 rounded text-[10px] ${TYPE_STYLE[c.orgType || ''] || TYPE_STYLE.client}`}>
              {ORG_TYPES.find((t) => t.value === c.orgType)?.label || 'untyped'}
            </span>
            {c.orgId && (
              <span className="font-mono text-[10px] text-gray-400">{c.orgId}</span>
            )}
            {archived && (
              <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300">
                archived
              </span>
            )}
            {c.industry && (
              <span className="text-xs text-gray-500 dark:text-gray-400">{c.industry}</span>
            )}
          </div>
          {/* Ownership and service, side by side and labelled — the two are
              routinely confused and the consequence of confusing them is who
              can see what. */}
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-xs text-gray-500 dark:text-gray-400">
            {group && <span>parent <strong>{group.name}</strong></span>}
            {partner && (
              <span className="flex items-center gap-1">
                <Handshake size={11} /> sold by <strong>{partner.name}</strong>
              </span>
            )}
            {c.region && <span>residency <strong>{c.region}</strong></span>}
            {c.regionCode && <span>pricing <strong>{c.regionCode}</strong></span>}
            {c.website && (
              <span className="flex items-center gap-1"><Globe size={11} />{c.website}</span>
            )}
          </div>
          {(c.contacts || []).length > 0 && (
            <div className="flex flex-wrap gap-3 mt-1.5">
              {c.contacts.map((p, i) => (
                <span key={i} className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                  <Mail size={11} /> {p.name}{p.email ? ` · ${p.email}` : ''}
                </span>
              ))}
            </div>
          )}
        </button>
        <div className="shrink-0 flex items-center gap-1">
          <button
            onClick={onArchive}
            className="text-gray-300 dark:text-gray-600 hover:text-accent"
            title={archived ? 'Restore' : 'Archive (keeps history intact)'}
          >
            {archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
          </button>
          <button
            onClick={onDelete}
            className="text-gray-300 dark:text-gray-600 hover:text-red-500"
            title="Delete permanently — refused if anything depends on it"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function ClientModal({ client, all, onClose, onSaved }: {
  client: CrmClient | null;
  all: CrmClient[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: client?.name || '',
    industry: client?.industry || '',
    website: client?.website || '',
    regionCode: client?.regionCode || 'US',
    region: client?.region || '',
    orgType: client?.orgType || 'client',
    parentOrgId: client?.parentOrgId || '',
    partnerOrgId: client?.partnerOrgId || '',
    notes: client?.notes || '',
  });
  const [contacts, setContacts] = useState<CrmContact[]>(client?.contacts || []);
  const [registration, setRegistration] = useState<CrmRegistration>(client?.registration || {});
  const [remittance, setRemittance] = useState<CrmRemittance>(client?.remittance || {});
  const [billing, setBilling] = useState<CrmBillingContact>(client?.billingContact || {});
  const [paymentTermsDays, setPaymentTermsDays] = useState(
    client?.paymentTermsDays != null ? String(client.paymentTermsDays) : '');
  const setReg = (k: keyof CrmRegistration, v: string) => setRegistration((r) => ({ ...r, [k]: v }));
  const setRemit = (k: keyof CrmRemittance, v: string) => setRemittance((r) => ({ ...r, [k]: v }));
  const [saving, setSaving] = useState(false);

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const setContact = (i: number, k: keyof CrmContact, v: string) =>
    setContacts((cs) => cs.map((c, j) => (j === i ? { ...c, [k]: v } : c)));

  // An account never appears in its OWN pickers: it cannot be its own parent or
  // its own partner, and offering the option only to have the server refuse it
  // wastes the round trip.
  const others = all.filter((c) => c._id !== client?._id && c.orgId
    && c.status !== 'archived');
  // Only a reseller or a group may service an account. A `client` is excluded
  // because a partner-over-client grant follows this edge, so pointing it at a
  // client would give one client sight of another.
  const canService = others.filter((c) => c.orgType === 'reseller' || c.orgType === 'parent');

  const save = async () => {
    if (!form.name.trim()) {
      toast.error('An organisation needs a name');
      return;
    }
    setSaving(true);
    try {
      // Contacts are sent whole — the server replaces the list rather than
      // merging, so a removed contact stays removed.
      const payload = {
        ...form, contacts: contacts.filter((c) => c.name || c.email), registration,
        // Research syncs this person as a billing-only user (EP-44 FE-2 ST-3); empty clears it.
        billingContact: billing.email ? billing : {},
        // Only our own companies carry remittance; sending it for anyone else
        // is refused by the server.
        ...(form.orgType === 'parent' ? {
          remittance,
          paymentTermsDays: paymentTermsDays === '' ? null : Number(paymentTermsDays),
        } : {}),
      };
      const saved = client
        ? await crmApi.updateClient(client._id, payload)
        : await crmApi.createClient(payload);
      // The RBAC mirror is reported rather than assumed: it writes the edge
      // that decides who can see this account's work, and a partner assignment
      // that did not reach the access model must not look like a success.
      if (saved.orgSync?.error) {
        toast.error(`Saved, but the access mirror did not: ${saved.orgSync.error}`,
          { duration: 9000 });
      } else {
        toast.success(client ? 'Organisation updated' : 'Organisation created');
      }
      onSaved();
    } catch (err: any) {
      // The server's refusals name the actual problem (immutable orgId,
      // ownership cycle, a client used as a partner) — shown verbatim rather
      // than replaced with something generic.
      toast.error(err.message || 'Could not save', { duration: 9000 });
    } finally {
      setSaving(false);
    }
  };

  const field = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent';
  const label = 'block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1';
  const hint = 'mt-1 text-[11px] text-gray-400 dark:text-gray-500';

  const typeHint = ORG_TYPES.find((t) => t.value === form.orgType)?.hint || '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {client ? 'Edit organisation' : 'New organisation'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Identity is READ-ONLY and shown, not hidden. `orgId` is the
              estate's tenancy key: it keys budgets, token allowlists and
              idempotency namespaces in other systems, so the service refuses to
              change it. Someone reading it off a support ticket needs to see
              it; nobody needs to edit it. */}
          {client && (
            <div className="flex items-center gap-4 rounded-lg bg-gray-50 dark:bg-gray-900/40 px-3 py-2">
              <Lock size={12} className="text-gray-400 shrink-0" />
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs">
                <span className="text-gray-500 dark:text-gray-400">
                  orgId <strong className="font-mono text-gray-800 dark:text-gray-200">
                    {client.orgId || '—'}
                  </strong>
                </span>
                {client.displayId && (
                  <span className="text-gray-500 dark:text-gray-400">
                    id <strong className="font-mono text-gray-800 dark:text-gray-200">
                      {client.displayId}
                    </strong>
                  </span>
                )}
                <span className="text-gray-400">immutable — it is the tenancy key</span>
              </div>
            </div>
          )}

          <div>
            <label className={label}>Name</label>
            <input className={field} value={form.name}
              onChange={(e) => set('name', e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Type</label>
              <select className={field} value={form.orgType}
                onChange={(e) => set('orgType', e.target.value)}>
                {ORG_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <p className={hint}>{typeHint}</p>
            </div>
            <div>
              <label className={label}>Industry</label>
              <input className={field} value={form.industry}
                onChange={(e) => set('industry', e.target.value)} />
            </div>
          </div>

          {/* Two regions, deliberately adjacent and labelled. `regionCode`
              answers "what does this cost here?"; `region` answers "where may
              this organisation's data live?". Conflating them would let a price
              list decide a residency boundary. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Pricing region</label>
              <input className={field} value={form.regionCode}
                onChange={(e) => set('regionCode', e.target.value.toUpperCase())} />
              <p className={hint}>Catalogue region — what it costs. 'US', 'GB', 'ZA'.</p>
            </div>
            <div>
              <label className={label}>Data residency</label>
              <select className={field} value={form.region}
                onChange={(e) => set('region', e.target.value)}>
                {RESIDENCY.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              <p className={hint}>Where this org's data may live. Not a price list.</p>
            </div>
          </div>

          {/* Ownership and service. Separate controls, separate explanations —
              an account can belong to one group and be sold by a reseller
              outside it, and neither can be derived from the other. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Parent organisation</label>
              <select className={field} value={form.parentOrgId}
                onChange={(e) => set('parentOrgId', e.target.value)}>
                <option value="">— none —</option>
                {others.map((c) => (
                  <option key={c._id} value={c.orgId}>{c.name} · {c.orgId}</option>
                ))}
              </select>
              <p className={hint}>Who this account belongs to — e.g. 5MOS under West42.</p>
            </div>
            <div>
              <label className={label}>
                <Handshake size={10} className="inline mr-1" />Partner (reseller)
              </label>
              <select className={field} value={form.partnerOrgId}
                onChange={(e) => set('partnerOrgId', e.target.value)}>
                <option value="">— none —</option>
                {canService.map((c) => (
                  <option key={c._id} value={c.orgId}>{c.name} · {c.orgId}</option>
                ))}
              </select>
              <p className={hint}>
                Who sells and services it. Grants that partner sight of this
                account's work, and opportunities default from it.
              </p>
            </div>
          </div>

          {form.parentOrgId && form.parentOrgId === form.partnerOrgId && (
            <p className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-500">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              The owner and the servicer must be different organisations —
              ownership and service are separate relationships. This will be
              refused on save.
            </p>
          )}
          {form.orgType === 'reseller' && !client && (
            <p className="text-[11px] text-gray-400 dark:text-gray-500">
              This creates a partner. It becomes selectable on other accounts and
              on opportunities as soon as it is saved.
            </p>
          )}

          {/* What a quote or invoice must say about this company — printed in
              their headers. Left blank until verified: a guessed registration
              number ends up on a contract. */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
            <div className="text-xs font-semibold text-gray-700 dark:text-gray-200">
              Legal &amp; registration
              <span className="ml-1 font-normal text-gray-400">— printed on quotes and invoices</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className={label}>Registered (legal) name</label>
                <input className={field} value={registration.registeredName || ''}
                  placeholder={form.name}
                  onChange={(e) => setReg('registeredName', e.target.value)} />
              </div>
              <div>
                <label className={label}>Company type</label>
                <input className={field} value={registration.companyType || ''}
                  placeholder="LLC, Inc., (Pty) Ltd"
                  onChange={(e) => setReg('companyType', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>Registration number</label>
                <input className={field} value={registration.registrationNumber || ''}
                  onChange={(e) => setReg('registrationNumber', e.target.value)} />
              </div>
              <div>
                <label className={label}>Jurisdiction</label>
                <input className={field} value={registration.jurisdiction || ''}
                  placeholder="Delaware, USA · England & Wales · South Africa"
                  onChange={(e) => setReg('jurisdiction', e.target.value)} />
              </div>
            </div>
            <div>
              <label className={label}>Registration authority</label>
              <input className={field} value={registration.registrationAuthority || ''}
                placeholder="Delaware Division of Corporations · Companies House · CIPC"
                onChange={(e) => setReg('registrationAuthority', e.target.value)} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={label}>Tax ID type</label>
                <input className={field} value={registration.taxIdLabel || ''}
                  placeholder="EIN, VAT, GST"
                  onChange={(e) => setReg('taxIdLabel', e.target.value)} />
              </div>
              <div className="col-span-2">
                <label className={label}>Tax ID</label>
                <input className={field} value={registration.taxId || ''}
                  onChange={(e) => setReg('taxId', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>Registered address</label>
                <textarea className={field} rows={3} value={registration.registeredAddress || ''}
                  onChange={(e) => setReg('registeredAddress', e.target.value)} />
              </div>
              <div>
                <label className={label}>Billing address</label>
                <textarea className={field} rows={3} value={registration.billingAddress || ''}
                  placeholder="If invoices go elsewhere"
                  onChange={(e) => setReg('billingAddress', e.target.value)} />
              </div>
            </div>
          </div>

          {/* How customers pay us — only on our own companies, printed on their
              invoices. Changing it needs org:manage on this company. */}
          {form.orgType === 'parent' && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
              <div className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                Remittance &amp; terms
                <span className="ml-1 font-normal text-gray-400">— printed on this company's invoices</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {([
                  ['bankName', 'Bank'], ['accountName', 'Account name'],
                  ['accountNumber', 'Account number'], ['routingNumber', 'Routing (ABA)'],
                  ['swift', 'SWIFT / BIC'], ['iban', 'IBAN'], ['sortCode', 'Sort code'],
                ] as [keyof CrmRemittance, string][]).map(([key, text]) => (
                  <div key={key}>
                    <label className={label}>{text}</label>
                    <input className={field} value={remittance[key] || ''}
                      onChange={(e) => setRemit(key, e.target.value)} />
                  </div>
                ))}
                <div>
                  <label className={label}>Payment terms (days)</label>
                  <input className={field} inputMode="numeric" value={paymentTermsDays}
                    placeholder="30"
                    onChange={(e) => setPaymentTermsDays(e.target.value.replace(/[^0-9]/g, ''))} />
                </div>
              </div>
              <div>
                <label className={label}>Payment reference wording</label>
                <input className={field} value={remittance.paymentReference || ''}
                  placeholder="Please quote the invoice number"
                  onChange={(e) => setRemit('paymentReference', e.target.value)} />
              </div>
            </div>
          )}

          <div>
            <label className={label}>Website</label>
            <input className={field} value={form.website}
              onChange={(e) => set('website', e.target.value)} />
          </div>

          <div>
            <label className={label}>Billing contact <span className="font-normal text-gray-400">— sees billing in Research</span></label>
            <div className="grid grid-cols-3 gap-2">
              <input className={field} placeholder="Name" value={billing.name || ''}
                onChange={(e) => setBilling((b) => ({ ...b, name: e.target.value }))} />
              <input className={field} placeholder="Email" value={billing.email || ''}
                onChange={(e) => setBilling((b) => ({ ...b, email: e.target.value }))} />
              <input className={field} placeholder="Phone" value={billing.phone || ''}
                onChange={(e) => setBilling((b) => ({ ...b, phone: e.target.value }))} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={label}>Contacts</label>
              <button
                onClick={() => setContacts((cs) => [...cs, { name: '', email: '', role: '' }])}
                className="text-xs text-accent"
              >
                + Add contact
              </button>
            </div>
            <div className="space-y-2">
              {contacts.map((c, i) => (
                <div key={i} className="flex gap-2">
                  <input className={field} placeholder="Name" value={c.name || ''}
                    onChange={(e) => setContact(i, 'name', e.target.value)} />
                  <input className={field} placeholder="Email" value={c.email || ''}
                    onChange={(e) => setContact(i, 'email', e.target.value)} />
                  <button
                    onClick={() => setContacts((cs) => cs.filter((_, j) => j !== i))}
                    className="shrink-0 px-2 text-gray-400 hover:text-red-500"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              {contacts.length === 0 && (
                <p className="text-xs text-gray-400">No contacts yet.</p>
              )}
            </div>
          </div>

          <div>
            <label className={label}>Notes</label>
            <textarea className={field} rows={3} value={form.notes}
              onChange={(e) => set('notes', e.target.value)} />
          </div>

          {client?._id ? <UsageRecords clientId={client._id} /> : null}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
          <button onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            className="px-3 py-1.5 text-sm rounded-lg bg-accent text-white disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}


/** Monthly usage from Research (EP-44 FE-2): each frozen month, its Analysis Units and the draft or
 * issued invoice raised from it. Read-only: a month is never edited, only adjusted next month. */
function UsageRecords({ clientId }: { clientId: string }) {
  const [rows, setRows] = useState<CrmUsageRecord[] | null>(null);
  useEffect(() => {
    crmApi.clientUsage(clientId).then((r) => setRows(r.records)).catch(() => setRows([]));
  }, [clientId]);
  if (!rows || rows.length === 0) return null;
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Research usage</label>
      <table className="w-full text-xs">
        <thead className="text-left text-gray-500">
          <tr><th className="py-1 pr-2 font-medium">Month</th><th className="py-1 pr-2 font-medium text-right">AU</th>
            <th className="py-1 pr-2 font-medium text-right">Included</th><th className="py-1 pr-2 font-medium text-right">Price</th>
            <th className="py-1 font-medium">Invoice</th></tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700 text-gray-700 dark:text-gray-200">
          {rows.map((r) => (
            <tr key={r._id} title={(r.lines || []).map((l) => `${l.subdimension || l.dimension || l.framework}: ${l.au} AU`).join(' · ')}>
              <td className="py-1 pr-2">{r.period}</td>
              <td className="py-1 pr-2 text-right font-mono">{r.quantity}</td>
              <td className="py-1 pr-2 text-right font-mono text-gray-400">{r.includedAu}</td>
              <td className="py-1 pr-2 text-right font-mono">{r.unitPrice} {r.currency}</td>
              <td className="py-1">{r.invoice ? `${r.invoice.number || 'draft'} · ${r.invoice.status}` : 'not billed'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
